import 'particles.js';

// PIXI canvas support for disabled/missing WebGL
import 'pixi.js-legacy';
import './setImmediate';
import { escalateReactWarnings } from '@ipfabric/frontend-shared-assets/utils/react/escalateReactWarnings';

// Import all the third party stuff
import { QueryClientProvider } from '@tanstack/react-query';
import { AppInitializer } from 'components/AppInitializer/AppInitializer';
import { AppTheme } from 'components/AppTheme/AppTheme';
import { DevToolbar } from 'components/DevToolbar/DevToolbar';
import { GlobalState } from 'components/GlobalState/GlobalState';
import { Locale } from 'components/Locale/Locale';
import ErrorBoundary from 'containers/ErrorBoundary';
import React, { type FC, type ReactNode } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import type { DndProviderProps } from 'react-dnd/lib/common/DndProvider';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { HistoryRouter } from 'redux-first-history/rr6';
import { queryClient } from 'utils/react-query/queryClient';

if (process.env.NODE_ENV === 'development') {
  const whyDidYouRender = require('@welldone-software/why-did-you-render');
  whyDidYouRender(React);
  escalateReactWarnings();
}

// Import root app
import { App } from 'containers/App/App';

// Import Language Provider
import LanguageProvider from 'containers/LanguageProvider';

import { history, store } from './store';

// Import i18n messages
import { translationMessages } from './i18n';

import { enableMapSet, setAutoFreeze } from 'immer';
enableMapSet();
setAutoFreeze(false);

// TODO NIM-9341 BC breaking update of react-dnd needed to fix types
const DndProviderFixed = DndProvider as FC<
  DndProviderProps<any, any> & { children?: ReactNode }
>;

const render = (messages: object | null | undefined) => {
  const rootElement = document.getElementById('app');
  if (rootElement !== null) {
    const root = createRoot(rootElement);
    root.render(
      <QueryClientProvider client={queryClient}>
        <HistoryRouter history={history}>
          <Locale>
            <Provider store={store}>
              <ErrorBoundary>
                <GlobalState>
                  <AppTheme>
                    <AppInitializer>
                      <DndProviderFixed backend={HTML5Backend}>
                        <LanguageProvider messages={messages}>
                          <App />
                        </LanguageProvider>
                      </DndProviderFixed>
                    </AppInitializer>
                  </AppTheme>
                </GlobalState>
              </ErrorBoundary>
            </Provider>
          </Locale>
        </HistoryRouter>
        {process.env.NODE_ENV === 'development' && <DevToolbar />}
      </QueryClientProvider>,
    );
  }
};

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  new Promise((resolve) => {
    resolve(import('intl'));
  })
    .then(() => Promise.all([import('intl/locale-data/jsonp/en.js')]))
    .then(() => render(translationMessages))
    .catch((err) => {
      throw err;
    });
} else {
  render(translationMessages);
}
